// ChannelAnalysis.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Text,
  HStack,
  Button,
  Select,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import CustomDropdown from "../../../../components/CustomDropdown";
import { useColorMode } from "@chakra-ui/react";
import NetSentimentWave from "../Chart/NetSentimentWave";
import { useNetSentimentSeries, Mode } from "../Chart/useNetSentimentSeries";
import { useChannelMessages } from "../Chart/useChannelMessages";
import WaveLoader from "../../../../components/WaveLoader";
import { ChannelLite } from "./ChannelDetails";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../firebase/firebase";

const YEARS = Array.from(
  { length: 5 },
  (_, i) => new Date().getFullYear() - 4 + i
);
const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString("default", { month: "short" }),
}));

interface Props {
  selectedChannel: ChannelLite | null;
  users?: Array<{ id: string; firstName?: string; lastName?: string; displayName?: string; name?: string }>;
}

const ChannelAnalysis: React.FC<Props> = ({ selectedChannel, users = [] }) => {
  const channelId = selectedChannel?.id ?? null;
  const { msgs, isLoading } = useChannelMessages(channelId);
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const [channelUserNames, setChannelUserNames] = useState<string[]>([]);

  const [mode, setMode] = useState<Mode>("today");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 2;

  const data = useNetSentimentSeries(channelId, mode, { year, month });

  // Fetch assigned user names for the channel
  useEffect(() => {
    const fetchChannelUserNames = async () => {
      if (!selectedChannel?.id || !selectedChannel.organizationId || !selectedChannel.projectId) {
        setChannelUserNames([]);
        return;
      }
      try {
        // Fetch channel document to get assignedUsers
        const channelRef = doc(db, `organization/${selectedChannel.organizationId}/projects/${selectedChannel.projectId}/channels/${selectedChannel.id}`);
        const channelDoc = await getDoc(channelRef);
        if (!channelDoc.exists()) {
          setChannelUserNames([]);
          return;
        }
        const channelData = channelDoc.data();
        const assignedUserIds: string[] = channelData.assignedUsers || [];
        // Only process users who exist in the users collection
        const validUserIds = assignedUserIds.filter(userId => 
          users.some(user => user.id === userId)
        );
        
        // Fetch user names for each valid userId
        const userNames: string[] = [];
        for (const userId of validUserIds) {
          const userRef = doc(db, "users", userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = [userData.firstName, userData.lastName].filter(Boolean).join(" ") || userData.displayName || userData.name || userId;
            userNames.push(name);
          } else {
            // If user exists in users collection but not in users collection, show as "Deleted User"
            userNames.push("Deleted User");
          }
        }
        setChannelUserNames(userNames);
      } catch (err) {
        setChannelUserNames([]);
      }
    };
    fetchChannelUserNames();
  }, [selectedChannel, users]);

  // Pagination logic for users list (using channelUserNames)
  const indexOfLastUser = currentPage * USERS_PER_PAGE;
  const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
  const currentUsers = channelUserNames.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(channelUserNames.length / USERS_PER_PAGE);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Calculate stats for summary boxes
  const positiveMsgs = msgs.filter((m) => m.score > 0);
  const negativeMsgs = msgs.filter((m) => m.score < 0);
  const neutralMsgs = msgs.filter((m) => m.score === 0);
  const totalMsgs = msgs.length;
  const positiveCount = positiveMsgs.length;
  const negativeCount = negativeMsgs.length;
  const neutralCount = neutralMsgs.length;
  const positivePercent = totalMsgs
    ? ((positiveCount / totalMsgs) * 100).toFixed(1)
    : 0;
  const negativePercent = totalMsgs
    ? ((negativeCount / totalMsgs) * 100).toFixed(1)
    : 0;
  const neutralPercent = totalMsgs
    ? ((neutralCount / totalMsgs) * 100).toFixed(1)
    : 0;
  // User with highest positive/negative
  const userPosCount: Record<string, number> = {};
  const userNegCount: Record<string, number> = {};
  msgs.forEach((m) => {
    if (m.score > 0)
      userPosCount[m.userName] = (userPosCount[m.userName] || 0) + 1;
    if (m.score < 0)
      userNegCount[m.userName] = (userNegCount[m.userName] || 0) + 1;
  });
  const topPositiveUser =
    Object.entries(userPosCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
  const topNegativeUser =
    Object.entries(userNegCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
  const allUsers = Array.from(new Set(msgs.map((m) => m.userName)));

  const donutData = [
    { name: "Positive", value: positiveCount },
    { name: "Negative", value: negativeCount },
  ];
  const donutColors = ["#68D391", "#FC8181"];

  const hasNoData = !isLoading && positiveCount === 0 && negativeCount === 0;

  if (!selectedChannel) return null;

  if (isLoading) {
    return (
      <Box
        bg={isDark ? "#1A1C1F" : "white"}
        height={"50vh"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        p={6}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
        textAlign="center"
      >
        <WaveLoader />
      </Box>
    );
  }

  if (hasNoData) {
    return (
      <Box
        bg={isDark ? "#1A1C1F" : "white"}
        height={"50vh"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        p={6}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
        textAlign="center"
      >
        <Text color={isDark ? "#C3C9D3" : "gray.500"} fontSize="md" fontWeight="medium">
          No sentiment data available for this channel
        </Text>
      </Box>
    );
  }

  if (msgs.length === 0) {
    return (
      <Alert
        status="info"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        bg={"transparent"}
        p={6}
      >
        <AlertIcon boxSize="40px" mb={2} />
        <AlertTitle>No messages yet</AlertTitle>
        <AlertDescription>
          The "{selectedChannel.name}" channel is quiet. Start the conversation!
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box bg={isDark ? "#1A1C1F" : "white"} px={2} pt={3} borderRadius="md" boxShadow="sm" borderWidth="1px" borderColor={isDark ? "#2E2F33" : "#E5E7EB"}>
      <Text fontSize="md" fontWeight="semibold" textAlign="center" mb={4}>
        {selectedChannel.name}'s Sentiment Analysis
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={5}>
        {/* OverAll Card */}
        <Stat
          p={3}
          borderRadius="lg"
          boxShadow="sm"
          bgGradient={isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear(to-br, #FFF7E6, white)"}
          display="flex"
          flexDirection="column"
          px={8}
          minH="220px"
        >
          <StatLabel
            textAlign="center"
            w="100%"
            fontSize="sm"
            fontWeight="semibold"
            mb={3}
            pb={2}
            borderBottom="2px solid"
            borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
            color={isDark ? "white" : "#111827"}
          >
            OverAll
          </StatLabel>
          <Box className="flex flex-col items-center w-full flex-1 justify-center">
            <Box w="100px" h="100px" position="relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    innerRadius={32}
                    outerRadius={45}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {donutData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={donutColors[idx]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box w="full" borderTop="1px" borderColor="gray.200" my={2}></Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              w="100%"
              mt={1}
              gap={4}
            >
              <Text
                color="green.500"
                fontSize="xs"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
              >
                Positive: {positiveCount}
              </Text>
              <Text
                color="red.500"
                fontSize="xs"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
              >
                Negative: {negativeCount}
              </Text>
            </Box>
            <Text mt={2} fontSize="xs" color="gray.400">
              Total Messages: {totalMsgs}
            </Text>
          </Box>
        </Stat>
        {/* Percentage Card */}
        <Stat
          p={3}
          borderRadius="lg"
          boxShadow="sm"
          bgGradient={isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear(to-br, #FFF7E6, white)"}
          display="flex"
          flexDirection="column"
          px={10}
          justifyContent="flex-start"
          minH="220px"
          width={"100%"}
        >
          <StatLabel
            textAlign="center"
            w="100%"
            fontSize="sm"
            fontWeight="semibold"
            mb={3}
            pb={2}
            borderBottom="2px solid"
            borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
            color={isDark ? "white" : "#111827"}
          >
            Percentage
          </StatLabel>
          <Box className="flex flex-col w-full flex-1 ">
            <Box w="full" display="flex" flexDirection="column" gap={3} mt={1}>
              {/* Positive bar */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                w="full"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  w="full"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Text color="green.500" fontWeight="medium" fontSize="xs">
                    Positive
                  </Text>
                  <Text color="green.500" fontWeight="bold" fontSize="xs">
                    {positivePercent}%
                  </Text>
                </Box>
                <Box
                  position="relative"
                  w="full"
                  h="12px"
                  bg={isDark ? "#2A3441" : "#FAEDD0"}
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    bg="#B59143"
                    borderRadius="full"
                    transition="all 0.5s"
                    width={`${positivePercent}%`}
                  />
                </Box>
              </Box>
              {/* Negative bar */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                w="full"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  w="full"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Text color="red.500" fontWeight="medium" fontSize="xs">
                    Negative
                  </Text>
                  <Text color="red.500" fontWeight="bold" fontSize="xs">
                    {negativePercent}%
                  </Text>
                </Box>
                <Box
                  position="relative"
                  w="full"
                  h="12px"
                  bg={isDark ? "#2A3441" : "#FAEDD0"}
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    bg="#B59143"
                    borderRadius="full"
                    transition="all 0.5s"
                    width={`${negativePercent}%`}
                  />
                </Box>
              </Box>
              {/* Neutral bar */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                w="full"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  w="full"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Text color="gray.500" fontWeight="medium" fontSize="xs">
                    Neutral
                  </Text>
                  <Text color="gray.500" fontWeight="bold" fontSize="xs">
                    {neutralPercent}%
                  </Text>
                </Box>
                <Box
                  position="relative"
                  w="full"
                  h="12px"
                  bg={isDark ? "#2A3441" : "#FAEDD0"}
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    bg="#B59143"
                    borderRadius="full"
                    transition="all 0.5s"
                    width={`${neutralPercent}%`}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Stat>
        {/* User Trend Card */}
        <Stat
          p={3}
          borderRadius="lg"
          boxShadow="sm"
          bgGradient={isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear(to-br, #FFF7E6, white)"}
          display="flex"
          flexDirection="column"
          px={10}
          justifyContent="flex-start"
          minH="220px"
        >
          <StatLabel
            textAlign="center"
            w="100%"
            fontSize="sm"
            fontWeight="semibold"
            mb={3}
            pb={2}
            borderBottom="1px solid"
            borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
            color={isDark ? "white" : "#111827"}
          >
            User Trend
          </StatLabel>
          <Box className="flex flex-col items-center w-full flex-1 justify-center">
            <Box
              display="flex"
              flexDirection="column"
              gap={3}
              mt={1}
              w="full"
              alignItems="center"
            >
              {topPositiveUser !== "-" && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  w="full"
                >
                  <Text
                    color="green.500"
                    fontWeight="medium"
                    fontSize="xs"
                    mb={1}
                  >
                    Most Positive User:
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={"center"}
                    gap={3}
                    w="full"
                    bg={isDark ? "#14161B" : "white"}
                    borderRadius="lg"
                    px={3}
                    py={2}
                  >
                    <Text
                      color="green.500"
                      fontWeight="medium"
                      flex={1}
                      textAlign="left"
                      fontSize="xs"
                    >
                      {topPositiveUser}
                    </Text>
                    <Text fontSize="xs" color="green.500" fontWeight="bold">
                      +{userPosCount[topPositiveUser]}
                    </Text>
                  </Box>
                </Box>
              )}
              {topNegativeUser !== "-" && (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent={"center"}
                  alignItems="center"
                  w="full"
                >
                  <Text
                    color="red.500"
                    fontWeight="medium"
                    fontSize="xs"
                    mb={1}
                  >
                    Most Negative User:
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={3}
                    w="full"
                    bg={isDark ? "#14161B" : "white"}
                    borderRadius="lg"
                    px={3}
                    py={2}
                  >
                    <Text
                      color="red.500"
                      fontWeight="medium"
                      flex={1}
                      textAlign="left"
                      fontSize="xs"
                    >
                      {topNegativeUser}
                    </Text>
                    <Text fontSize="xs" color="red.500" fontWeight="bold">
                      -{userNegCount[topNegativeUser]}
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Stat>
        {/* Users List Card */}
        <Stat
          p={3}
          borderRadius="lg"
          boxShadow="sm"
          bgGradient={isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear(to-br, #FFF7E6, white)"}
          display="flex"
          flexDirection="column"
          px={10}
          justifyContent="flex-start"
          minH="220px"
        >
          <StatLabel
            textAlign="center"
            w="100%"
            fontSize="sm"
            fontWeight="semibold"
            mb={3}
            pb={2}
            borderBottom="1px solid"
            borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
            color={isDark ? "white" : "#111827"}
          >
            Users List
          </StatLabel>
          <Box className="flex flex-col w-full flex-1" px={4} pt={2}>
            <Text fontSize="xs" color={isDark ? "white" : "#111827"} fontWeight="medium" mb={2}>
              Total Users: {channelUserNames.length}
            </Text>
            <Box w="full" display="flex" flexDirection="column" gap={1}>
              {channelUserNames.length > 0 ? (
                currentUsers.map((user, idx) => (
                  <Box
                    key={user}
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1}
                    w="full"
                  >
                    <Text fontSize="xs" color={isDark ? "white" : "#111827"} w="15%" textAlign="left">
                      {idx + indexOfFirstUser + 1}.
                    </Text>
                    <Text 
                      fontWeight="medium" 
                      fontSize="xs" 
                      color={isDark ? (user === "Deleted User" ? "red.400" : "white") : (user === "Deleted User" ? "red.500" : "gray.800")} 
                      w="80%" 
                      textAlign="left"
                    >
                      {user}
                    </Text>
                  </Box>
                ))
              ) : (
                <Text color="gray.400">-</Text>
              )}
            </Box>
            {/* Pagination Controls */}
            {channelUserNames.length > USERS_PER_PAGE && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                w="full"
                mt={4}
                px={4}
              >
                {/* <Text fontSize="xs" color="gray.600">
                  Showing {indexOfFirstUser + 1}-
                  {Math.min(indexOfLastUser, channelUserNames.length)} of{" "}
                  {channelUserNames.length} users
                </Text> */}
                <Box>
                  <Button
                    size="xs"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    mr={2}
                  >
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 19l-7-7 7-7"
                      ></path> 
                    </svg>
                  </Button>
                  <Button
                    size="xs"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      ></path>
                    </svg>
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Stat>
      </SimpleGrid>
      {/* Mode/Month/Year Dropdowns */}
      <div className="flex flex-row items-center gap-4 w-full mb-4">
        <Text fontSize="sm" fontWeight="medium" color={isDark ? "#C3C9D3" : "gray.700"} mr={2}>Filter :</Text>
        <div className="relative w-[150px]">
          <CustomDropdown
            value={mode}
            onChange={(value) => setMode(value as Mode)}
            isDark={isDark}
            className="w-full h-[30px] pr-8 px-4 py-1 text-xs font-normal rounded-xl border-2 shadow-sm focus:outline-none focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 hover:border-[#B59143]"
            options={[
              { value: "today", label: "Today" },
              { value: "weekly", label: "Weekly" },
              { value: "monthly", label: "Monthly" },
              { value: "yearly", label: "Yearly" }
            ]}
          />
        </div>
        {(mode === "weekly" || mode === "monthly") && (
          <div className="relative w-[150px]">
            <CustomDropdown
              value={month.toString()}
              onChange={(value) => setMonth(+value)}
              isDark={isDark}
              className="w-full h-[30px] pr-8 px-4 py-1 text-xs font-normal rounded-xl border-2 shadow-sm focus:outline-none focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 hover:border-[#B59143]"
              options={MONTHS.map((mo) => ({ value: mo.value.toString(), label: mo.label }))}
            />
          </div>
        )}
        {mode !== "today" && mode !== "overall" && (
          <div className="relative w-[150px]">
            <CustomDropdown
              value={year.toString()}
              onChange={(value) => setYear(+value)}
              isDark={isDark}
              className="w-full h-[30px] pr-8 px-4 py-1 text-xs font-normal rounded-xl border-2 shadow-sm focus:outline-none focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 hover:border-[#B59143]"
              options={YEARS.map((y) => ({ value: y.toString(), label: y.toString() }))}
            />
          </div>
        )}
      </div>
      <Box>
        <NetSentimentWave data={data} isChannelView={true} />
      </Box>
    </Box>
  );
};

export default ChannelAnalysis;
